import { SellerSettings } from '../enums/seller-settings/seller-settings';
import { updateSellerSettingsBulk } from '../pages/settings/api/save-settings';
import { getAutoInsureReturnSettingKey, useWalletedInsuranceProviderId } from '../services/insurance-service';
import { useSellerSetting } from './use-seller-setting';

const isSettingTrue = (value: string | undefined): boolean => value?.trim() === 'true';
const toSettingString = (value: boolean): string => (value ? 'true' : 'false');

export const RETURN_INSURANCE_PREFERENCE = {
  INSURE_ALL: 'insure_all',
  NONE: 'none',
} as const;

export type ReturnInsurancePreference = (typeof RETURN_INSURANCE_PREFERENCE)[keyof typeof RETURN_INSURANCE_PREFERENCE];

const deriveReturnInsurancePreference = (shouldAutoInsure: boolean): ReturnInsurancePreference =>
  shouldAutoInsure ? RETURN_INSURANCE_PREFERENCE.INSURE_ALL : RETURN_INSURANCE_PREFERENCE.NONE;

export const useReturnInsurancePreference = () => {
  const insuranceProviderId = useWalletedInsuranceProviderId();
  const returnAutoInsureKey = getAutoInsureReturnSettingKey(insuranceProviderId);

  const returnApplyParcelGuardSetting = useSellerSetting(SellerSettings.insuranceSystemAutomationReturnApplyParcelGuard);
  const returnApplyXCoverSetting = useSellerSetting(SellerSettings.insuranceSystemAutomationReturnApplyXCover);

  const shouldAutoInsure =
    returnAutoInsureKey === SellerSettings.insuranceSystemAutomationReturnApplyXCover.key
      ? isSettingTrue(returnApplyXCoverSetting)
      : isSettingTrue(returnApplyParcelGuardSetting);

  const preferenceNone = useSellerSetting(SellerSettings.insuranceSystemAutomationReturnPreferenceNone);
  const hasExplicitNonePreference = isSettingTrue(preferenceNone);

  const returnInsurancePreference = deriveReturnInsurancePreference(shouldAutoInsure);

  return {
    returnInsurancePreference,
    hasExplicitNonePreference,
  };
};

type SaveReturnInsurancePreferenceArgs = {
  preference: ReturnInsurancePreference;
  insuranceProviderId: string;
};

export const saveReturnInsurancePreference = async ({
  preference,
  insuranceProviderId,
}: SaveReturnInsurancePreferenceArgs): Promise<Record<string, string>> => {
  const autoInsureKey = getAutoInsureReturnSettingKey(insuranceProviderId);
  if (!autoInsureKey) {
    throw new Error(`Unknown insurance provider id: ${insuranceProviderId}`);
  }
  const settings: Record<string, string> = {
    [autoInsureKey]: toSettingString(preference === RETURN_INSURANCE_PREFERENCE.INSURE_ALL),
    [SellerSettings.insuranceSystemAutomationReturnPreferenceNone.key]: toSettingString(preference === RETURN_INSURANCE_PREFERENCE.NONE),
  };
  return updateSellerSettingsBulk(settings);
};
